/**
 * ShiftDialog — Add or edit a shift assignment
 * Supports multi-day selection when adding new shifts
 */

import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { ScheduleEntry, ShiftType } from '../../stores/schedule-store';
import { isAvondShift } from './shift-style';

interface ShiftDialogProps {
  staffName: string;
  date: string;
  weekDates?: string[];
  existingSchedule?: ScheduleEntry[];
  existing?: ScheduleEntry | null;
  onSave: (shiftType: ShiftType, timeFrom: string, timeTo: string) => Promise<boolean>;
  onSaveMultiple?: (dates: string[], shiftType: ShiftType, timeFrom: string, timeTo: string) => Promise<boolean>;
  onDelete?: () => Promise<boolean>;
  onClose: () => void;
  isSaving: boolean;
}

const presets: { type: ShiftType; label: string; from: string; to: string }[] = [
  { type: 'wasserij', label: 'Wasserij', from: '07:00', to: '16:00' },
  { type: 'wasserij', label: 'Wass. ochtend', from: '07:00', to: '11:30' },
  { type: 'wasserij', label: 'Wass. middag', from: '12:00', to: '16:00' },
  { type: 'wasserij', label: 'Wass. avond', from: '18:00', to: '21:30' },
  { type: 'route', label: 'Route', from: '06:00', to: '16:30' },
  { type: 'kantoor', label: 'Kantoor', from: '08:45', to: '13:00' },
];

const dayLabels: Record<string, string> = {
  '0': 'Zo', '1': 'Ma', '2': 'Di', '3': 'Wo', '4': 'Do', '5': 'Vr', '6': 'Za',
};

export function ShiftDialog({ staffName, date, weekDates, existingSchedule, existing, onSave, onSaveMultiple, onDelete, onClose, isSaving }: ShiftDialogProps) {
  const [shiftType, setShiftType] = useState<ShiftType>(existing?.shift_type || 'wasserij');
  const [timeFrom, setTimeFrom] = useState(existing?.time_from?.slice(0, 5) || '07:00');
  const [timeTo, setTimeTo] = useState(existing?.time_to?.slice(0, 5) || '16:00');
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set([date]));
  const [saved, setSaved] = useState(false);

  const isNew = !existing;
  const canMultiSelect = isNew && weekDates && weekDates.length > 0 && onSaveMultiple && staffName;

  const handlePreset = (preset: typeof presets[0]) => {
    setShiftType(preset.type);
    setTimeFrom(preset.from);
    setTimeTo(preset.to);
  };

  const toggleDate = (d: string) => {
    setSelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(d)) {
        if (next.size > 1) next.delete(d);
      } else {
        next.add(d);
      }
      return next;
    });
  };

  const selectAllDays = () => {
    if (!weekDates) return;
    // Select all days that don't already have a shift for this staff
    const available = weekDates.filter(d => {
      if (existingSchedule?.some(s => s.date === d)) return false;
      return true;
    });
    setSelectedDates(new Set(available));
  };

  const handleSave = async () => {
    if (canMultiSelect && selectedDates.size > 1) {
      const success = await onSaveMultiple!(Array.from(selectedDates), shiftType, timeFrom, timeTo);
      if (success) {
        setSaved(true);
        setTimeout(onClose, 800);
      }
    } else {
      const success = await onSave(shiftType, timeFrom, timeTo);
      if (success) {
        setSaved(true);
        setTimeout(onClose, 800);
      }
    }
  };

  const dateLabel = format(parseISO(date), 'EEEE d MMMM', { locale: nl });

  if (saved) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl text-center" onClick={e => e.stopPropagation()}>
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm font-medium text-main">
            {selectedDates.size > 1 ? `${selectedDates.size} diensten opgeslagen` : 'Dienst opgeslagen'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-main mb-1">
          {existing ? 'Dienst bewerken' : 'Dienst inplannen'}
        </h3>
        <p className="text-sm text-muted mb-4">
          {staffName ? `${staffName} \u00b7 ` : ''}{dateLabel}
        </p>

        {/* Multi-day selector (only for new shifts with a staff member) */}
        {canMultiSelect && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-muted">Dagen</label>
              <button
                onClick={selectAllDays}
                className="text-[11px] text-indigo-600 hover:text-indigo-800"
              >
                Selecteer alle vrije dagen
              </button>
            </div>
            <div className="flex gap-1.5">
              {weekDates.map(d => {
                const dow = new Date(d + 'T00:00:00').getDay();
                const dayLabel = dayLabels[String(dow)] || 'Zo';
                const dayNum = new Date(d + 'T00:00:00').getDate();
                const isSelected = selectedDates.has(d);
                const hasShift = existingSchedule?.some(s => s.date === d);

                return (
                  <button
                    key={d}
                    onClick={() => !hasShift && toggleDate(d)}
                    disabled={hasShift}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      hasShift
                        ? 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed'
                        : isSelected
                          ? 'bg-indigo-500 border-indigo-500 text-white'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    title={hasShift ? 'Heeft al een dienst' : ''}
                  >
                    <div>{dayLabel}</div>
                    <div className="text-[10px] opacity-70">{dayNum}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick presets */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {presets.map(preset => {
            const isActive = shiftType === preset.type && timeFrom === preset.from && timeTo === preset.to;
            return (
              <button
                key={`${preset.type}-${preset.from}`}
                onClick={() => handlePreset(preset)}
                className={`flex-1 min-w-0 py-2 rounded-lg text-xs font-medium border transition-colors ${
                  isActive
                    ? preset.type === 'wasserij' && isAvondShift(preset.from) ? 'bg-orange-100 border-orange-300 text-orange-800'
                      : preset.type === 'wasserij' ? 'bg-blue-100 border-blue-300 text-blue-800'
                      : preset.type === 'route' ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                      : 'bg-purple-100 border-purple-300 text-purple-800'
                    : 'bg-white border-gray-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        {/* Time inputs */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Van</label>
            <input
              type="time"
              value={timeFrom}
              onChange={e => setTimeFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Tot</label>
            <input
              type="time"
              value={timeTo}
              onChange={e => setTimeTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {isSaving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {isSaving
              ? 'Opslaan...'
              : selectedDates.size > 1
                ? `Opslaan (${selectedDates.size} dagen)`
                : 'Opslaan'
            }
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm transition-colors"
          >
            Annuleren
          </button>
          {existing && onDelete && (
            <button
              onClick={onDelete}
              disabled={isSaving}
              className="px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              Verwijder
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
